import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
  InputCurrency,
} from "@sellar-io/components";
import { DeliveryChargeRuleListResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import { Icon, SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { getFreeDeliveryBadge } from "../../../components/courierHaulierSwitchPrompt/pages/PalletDeliverySettingsPage.shared";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { useDeliveryChargeRuleCreate } from "../../../hooks/api/v2/deliveryChargeRule/useDeliveryChargeRuleCreate";
import { useDeliveryChargeRuleDelete } from "../../../hooks/api/v2/deliveryChargeRule/useDeliveryChargeRuleDelete";
import { useDeliveryChargeRuleList } from "../../../hooks/api/v2/deliveryChargeRule/useDeliveryChargeRuleList";
import { useDeliveryChargeRuleUpdate } from "../../../hooks/api/v2/deliveryChargeRule/useDeliveryChargeRuleUpdate";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../hooks/usePriceFormat";

type FreeDeliveryChargeRuleFulfillmentType =
  | "direct"
  | "courier"
  | "haulier"
  | "sellarDistribution";

export const FreeDeliveryChargeRuleCard = ({
  fulfillmentType,
}: {
  fulfillmentType: FreeDeliveryChargeRuleFulfillmentType;
}) => {
  const [dialogActive, setDialogActive] = useState(false);

  const deliveryChargeRulesListQuery = useDeliveryChargeRuleList({
    query: [
      {
        type: "fulfillmentType",
        modifier: "EQUALS",
        value: fulfillmentType,
      },
    ],
  });

  const deliveryChargeRule = deliveryChargeRulesListQuery.data?.pages[0]?.[0];

  if (deliveryChargeRulesListQuery.isLoading) return <SpinnerLegacy />;

  const getDeliveryRule = () => {
    if (fulfillmentType !== "haulier") {
      return typeof deliveryChargeRulesListQuery.data?.pages[0]?.[0]?.value ===
        "number"
        ? formatCurrency(
            deliveryChargeRulesListQuery.data.pages[0][0].value,
            "gbp",
          )
        : "Not set";
    }

    return getFreeDeliveryBadge(
      deliveryChargeRulesListQuery.data?.pages[0]?.[0]?.value,
    );
  };
  return (
    <>
      <Card className="bg-white">
        <CardHeader
          className={twMerge(
            "bg-white",
            "flex items-center justify-between p-4",
          )}
        >
          <div className="flex flex-col">
            <H5>Free delivery by basket subtotal</H5>
            <H6>
              Offer free delivery to your customers when their basket subtotal
              exceeds a certain amount. Calculated on the value of items in the
              customer’s basket before VAT and discounts.
            </H6>
          </div>
          <Button color={"secondary"} onClick={() => setDialogActive(true)}>
            <Icon icon={"PENCIL"} />
            Edit
          </Button>
        </CardHeader>
        <CardContent className={"flex flex-col gap-4 p-4"}>
          <p>{getDeliveryRule()}</p>
          {fulfillmentType === "haulier" ? (
            <Alert
              type="sellar"
              title="We recommend offering free delivery when customers spend £500 or more."
              message={
                "Encourage larger orders and make deliveries more efficient"
              }
              className="w-full bg-zinc-50"
            />
          ) : null}
        </CardContent>
      </Card>
      <Dialog active={dialogActive} onDismiss={() => setDialogActive(false)}>
        <FreeDeliveryChargeRuleDialogContent
          fulfillmentType={fulfillmentType}
          deliveryChargeRule={deliveryChargeRule}
          onDismiss={() => setDialogActive(false)}
        />
      </Dialog>
    </>
  );
};

const freeDeliveryThresholdFormId = v4();

const FreeDeliveryChargeRuleDialogContent = ({
  fulfillmentType,
  deliveryChargeRule,
  onDismiss,
}: {
  fulfillmentType: FreeDeliveryChargeRuleFulfillmentType;
  deliveryChargeRule: DeliveryChargeRuleListResponseType[number] | undefined;
  onDismiss: () => void;
}) => {
  const deliveryChargeRuleUpdate = useDeliveryChargeRuleUpdate(
    deliveryChargeRule?.id ?? 0,
  );

  const deliveryChargeRuleCreate = useDeliveryChargeRuleCreate();

  const deliveryChargeRuleDelete = useDeliveryChargeRuleDelete(
    deliveryChargeRule?.id ?? 0,
  );

  const form = useForm<{
    value: string;
  }>({
    shouldUseNativeValidation: true,
    defaultValues: {
      value:
        typeof deliveryChargeRule?.value === "number"
          ? formatPriceApiToInput(deliveryChargeRule.value)
          : undefined,
    },
  });

  return (
    <>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={`Free delivery by basket subtotal`}
      />
      <DialogContent>
        <form
          id={freeDeliveryThresholdFormId}
          onSubmit={form.handleSubmit((data) => {
            const apiValue = formatPriceInputToApi(data.value);

            if (apiValue === null) {
              return deliveryChargeRuleDelete.mutate(undefined, {
                onSuccess: () => onDismiss(),
              });
            }

            if (!deliveryChargeRule) {
              return deliveryChargeRuleCreate.mutate(
                {
                  fulfillmentType,
                  value: apiValue,
                },
                {
                  onSuccess: () => onDismiss(),
                },
              );
            }

            return deliveryChargeRuleUpdate.mutate(
              {
                value: apiValue,
              },
              {
                onSuccess: () => onDismiss(),
              },
            );
          })}
        >
          <InputLabel>Set threshold</InputLabel>
          <InputContainer>
            <InputCurrency
              showPrefix
              allowNegative
              className={"w-full"}
              placeholder={"No threshold"}
              {...form.register("value", {
                required: false,
                valueAsNumber: true,
                min: 0,
              })}
            />
          </InputContainer>
        </form>
      </DialogContent>
      <DialogCTA>
        <Button
          form={freeDeliveryThresholdFormId}
          type={"submit"}
          loading={
            deliveryChargeRuleDelete.isPending ||
            deliveryChargeRuleUpdate.isPending ||
            deliveryChargeRuleCreate.isPending
          }
        >
          Set
        </Button>
      </DialogCTA>
    </>
  );
};
